import { useState, useEffect, useMemo } from "react";
import Tabs from "components/core/basic/Tabs";
import { useAppSelector } from "redux/hooks";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "zodios/client";
import Loading from "components/core/blocks/Loading";
import type { ExpanderProps, Summary } from "./types";
import TranscriptTab from "./TranscriptTab";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";
import ActionsBox from "./components/Actions";
import SummaryBox from "./components/Summary";
import AttendeesBox from "./components/Attendees";
import SentimentBox from "./components/Sentiment";
import EmailBox from "./components/Email";
import SalesBox from "./components/Sales";
import { css } from "../../../styled-system/css";

const TranscriptExpander = ({ file_id, expanded, timeValue }: ExpanderProps) => {
  const orgId = useAppSelector((state) => state.user.organization_id);
  const transcriptData = useQuery({
    queryKey: ["transcript_data", orgId, file_id],
    queryFn: async () =>
      await apiClient.getTranscriptsResponse({
        params: { org_id: orgId, file_id: file_id },
      }),
  });

  type TabKey = "transcript" | "summary" | "email";
  const [selectedTab, setSelectedTab] = useState<TabKey>(timeValue ? "transcript" : "summary");

  useEffect(() => {
    if (timeValue) {
      setSelectedTab("transcript");
    } else {
      setSelectedTab("summary");
    }
  }, [timeValue]);

  const allTranscriptData = transcriptData.data;
  const callType = useMemo(() => {
    if (!allTranscriptData || !allTranscriptData.response) {
      return "Unknown";
    }
    const summaryItems = allTranscriptData.response.filter((item) => item.type === "summary");
    try {
      if (summaryItems[0]?.content) {
        const summary = JSON.parse(summaryItems[0].content) as Summary;

        return summary["Call Type"].toLocaleLowerCase() || "unknown";
      }
      return undefined;
    } catch {
      return undefined;
    }

    return "sales";
  }, [allTranscriptData]);

  if (!allTranscriptData || !allTranscriptData.response) {
    return (
      <div>
        <div className={css({ width: expanded ? "50%" : "100%", marginBottom: "3rem" })}>
          <Loading fullPage={false} height={300} />
        </div>
      </div>
    );
  }
  if (transcriptData.isError) {
    return <DevErrorDisplay userMessage={"Error loading transcript data"} error={transcriptData.error} />;
  }

  const SalesSummary = () => {
    return (
      <div>
        <div className={css({ width: "100%", marginBottom: "3rem" })}>
          <div
            className={css({
              border: "1px solid #ccc",
              borderRadius: "8px",
              minHeight: "100%",
              padding: "2rem",
              backgroundColor: "#f9f9f9",
            })}
          >
            <AttendeesBox transcriptData={allTranscriptData.response ?? []} />

            <SalesBox transcriptData={allTranscriptData.response ?? []} expanded={expanded} />
          </div>
        </div>
      </div>
    );
  };

  const Summary = () => {
    return (
      <div>
        <div
          className={css({ width: callType === "customer success" && expanded ? "50%" : "100%", marginBottom: "3rem" })}
        >
          <div
            className={css({
              border: "1px solid #ccc",
              borderRadius: "8px",
              minHeight: "100%",
              padding: "2rem",
              backgroundColor: "#f9f9f9",
            })}
          >
            <SentimentBox transcriptData={allTranscriptData.response ?? []} />

            <AttendeesBox transcriptData={allTranscriptData.response ?? []} />

            <SummaryBox transcriptData={allTranscriptData.response ?? []} />
          </div>
        </div>
        <div
          className={css({ width: callType === "customer success" && expanded ? "50%" : "100%", marginBottom: "3rem" })}
        >
          <div
            className={css({
              border: "1px solid #ccc",
              borderRadius: "8px",
              minHeight: "100%",
              padding: "2rem",
              backgroundColor: "#f9f9f9",
            })}
          >
            <ActionsBox transcriptData={allTranscriptData.response ?? []} />
          </div>
        </div>
      </div>
    );
  };

  const tabs = {
    transcript: (
      <fieldset>
        {allTranscriptData.response
          .filter((item) => item.type === "transcript")
          .map((item, idx) => {
            return <TranscriptTab key={idx} file_id={file_id} timeValue={timeValue} content={item.content} />;
          })}
      </fieldset>
    ),

    summary: callType === "sales" ? <SalesSummary /> : <Summary />,

    email: (
      <div
        className={css({
          border: "1px solid #ccc",
          borderRadius: "8px",
          minHeight: "100%",
          padding: "4rem",
          backgroundColor: "#f9f9f9",
        })}
      >
        <EmailBox transcriptData={allTranscriptData.response} />
      </div>
    ),
  };

  if (!expanded && callType === "sales") {
    return <SalesSummary />;
  } else if (!expanded && callType !== "sales") {
    return <Summary />;
  }

  return (
    <div className={css({ backgroundColor: "#fff" })}>
      <Tabs
        tabs={[
          { label: <span>Summary</span>, key: "summary" },
          { label: <span>Transcript</span>, key: "transcript" },
          { label: <span>Email</span>, key: "email" },
        ]}
        selectedTab={selectedTab}
        onTabChange={(tab) => setSelectedTab(tab as "transcript" | "summary" | "email")}
        bsClass={"mt-0 mb-5"}
      >
        {tabs[selectedTab]}
      </Tabs>
    </div>
  );
};

export default TranscriptExpander;
